import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "submit", "message", "spinner"]
  static values = {
    stripeKey: String,
    checkoutUrl: String,
    confirmationUrl: String
  }

  connect() {
    this.stripe = Stripe(this.stripeKeyValue)
    this.elements = null
    this.initialize()
  }

  async initialize() {
    try {
      const response = await fetch(this.checkoutUrlValue, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      })
      
      if (!response.ok) {
        throw new Error("Failed to fetch payment intent")
      }
      
      const { client_secret } = await response.json()
      
      if (!client_secret) {
        throw new Error("No client secret received")
      }

      this.elements = this.stripe.elements({ clientSecret: client_secret })
      const paymentElement = this.elements.create("payment")
      paymentElement.mount("#payment-element")
      
      // Check if we need to show any initial status
      this.checkStatus()
    } catch (error) {
      console.error("Payment initialization error:", error)
      this.showError("Failed to initialize payment form. Please try again.")
    }
  }

  async handleSubmit(event) {
    event.preventDefault()
    this.setLoading(true)

    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.confirmationUrlValue
        }
      })

      if (error) {
        this.showError(error.message)
      }
    } catch (error) {
      console.error("Payment submission error:", error)
      this.showError("An unexpected error occurred. Please try again.")
    }

    this.setLoading(false)
  }

  async checkStatus() {
    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    )

    if (!clientSecret) return

    try {
      const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret)

      switch (paymentIntent.status) {
        case "succeeded":
          window.location.href = this.confirmationUrlValue
          break
        case "processing":
          this.showMessage("Your payment is processing.")
          break
        case "requires_payment_method":
          this.showError("Your payment was not successful, please try again.")
          break
        default:
          this.showError("Something went wrong.")
          break
      }
    } catch (error) {
      console.error("Payment status check error:", error)
      this.showError("Failed to check payment status.")
    }
  }

  showError(message) {
    const messageDiv = this.messageTarget
    messageDiv.textContent = message
    messageDiv.classList.remove("hidden")
  }

  showMessage(message) {
    this.showError(message) // Reuse the error display for general messages
  }

  setLoading(isLoading) {
    const submitButton = this.submitTarget
    const spinner = this.spinnerTarget

    submitButton.disabled = isLoading
    if (isLoading) {
      submitButton.querySelector("#button-text").classList.add("hidden")
      spinner.classList.remove("hidden")
    } else {
      submitButton.querySelector("#button-text").classList.remove("hidden")
      spinner.classList.add("hidden")
    }
  }
}
